import { useMemo } from 'react';

import type { FormValues } from '#src/app/pages/petitionDetails/shared/types';
import { useCountryCode } from '#src/app/shared/hooks/l10n';
import { useSessionAsync } from '#src/app/shared/hooks/session';
import type { StateWithoutError } from '#src/app/shared/utils/async';
import { isLoading } from '#src/app/shared/utils/async';

import { normalizeGeolocalizationValues } from './normalizeGeolocalizationValues';

export type GeolocationValuesState = StateWithoutError<{
	values: Pick<FormValues, 'countryCode' | 'city' | 'postalCode' | 'stateCode'> | undefined;
}>;

export function useGeolocationNormalizedValues(): GeolocationValuesState {
	const countryCode = useCountryCode();
	const session = useSessionAsync();

	const loadingState = useMemo(() => ({ status: 'loading' as const }), []);
	const emptyState = useMemo(() => ({ status: 'loaded' as const, values: undefined }), []);

	return useMemo(() => {
		if (isLoading(session)) {
			return loadingState;
		}

		const { geoipData } = session.value;

		const city = geoipData.city ?? undefined;
		const postalCode = geoipData.postalCode ?? undefined;
		const stateCode = geoipData.region ?? undefined;

		if (!city) {
			return emptyState;
		}

		if (countryCode !== geoipData.countryCode) {
			return emptyState;
		}

		return {
			status: 'loaded',
			values: normalizeGeolocalizationValues({
				countryCode,
				city,
				postalCode,
				stateCode,
			}),
		};
	}, [countryCode, session, loadingState, emptyState]);
}
